import { AnyAction, DispatchFunction } from '@owc-utils/SeamlessStore';
import { State } from '@owc-types/ComponentInitializeDispatcher';
import { connect, getConnection, subscribe, ShimmedConnection } from '@/store';
import { Reducer } from 'redux';

export const componentNamespace = 'OWC_COMPONENT';
export const componentAdd = `${componentNamespace}/OWC_COMPONENT_ADD`;
export const componentUpdate = `${componentNamespace}/OWC_COMPONENT_UPDATE`;
export const componentsUpdate = `${componentNamespace}/OWC_COMPONENTS_UPDATE`;

export interface ComponentHashtable {
  [key: string]: Component;
}

export interface Component {
  id: string;
  parentId?: string;
  [key: string]: any;
}

export type DataTreeItem<DataJson> = Component & {
  childComponents: Component[] | undefined;
  json: DataJson;
};

export type TreeItem = Component & {
  childComponents: Component[] | undefined;
};

export type ComponentDispatchers = {
  addComponent: (payload: Component) => (dispatch: DispatchFunction) => void;
  updateComponent: (payload: any) => (dispatch: DispatchFunction) => void;
  updateComponents: (payload: any) => (dispatch: DispatchFunction) => void;
};

const addComponent = (payload: Component) => {
  return (dispatcher: DispatchFunction) => {
    dispatcher({
      type: componentAdd,
      payload,
    });
  };
};

const updateComponent = (payload: any) => {
  return (dispatcher: DispatchFunction) => {
    dispatcher({
      type: componentUpdate,
      payload,
    });
  };
};

const updateComponents = (payload: any) => {
  return (dispatcher: DispatchFunction) => {
    dispatcher({
      type: componentsUpdate,
      payload,
    });
  };
};

const updateState = (state: State, payload: any, transformItem: (comp: Component, payload: any) => any) => {
  const found = state.find(component => component.id === payload.id);
  let newState = [];
  if (found) {
    newState = state.map(item => {
      let newObj: any = {};
      if (item.id === payload.id) {
        newObj = transformItem(item, payload);
      } else {
        newObj = { ...item };
      }
      return newObj;
    });
  } else {
    newState = [...state, payload];
  }
  return newState;
};

const transform = (item: any, payload: any) => {
  const json = { ...item.json, ...payload.json };
  const toMerge = { json };
  return Object.assign({}, item, payload, toMerge);
};

const mergeState = (state: State, payload: any) => {
  return updateState(state, payload, transform);
};

const mergeComponentState = (state: State, payload: any) => {
  return updateState(state, payload, transform);
};

const mergeComponentsState = (state: State, payload: any[]) => {
  let newState = [...state];
  payload.forEach(component => {
    newState = mergeComponentState(newState, component);
  });
  return newState;
};

const reducer: Reducer<State> = (state: State = [], action: AnyAction = { type: '' }) => {
  const { type, payload = {} } = action;
  switch (type) {
    case componentAdd:
      return mergeState(state, payload);
    case componentUpdate:
      return mergeComponentState(state, payload);
    case componentsUpdate:
      return mergeComponentsState(state, payload);
    default:
      return state;
  }
};

const dispatchers: ComponentDispatchers = {
  addComponent: (payload: Component) => addComponent(payload),
  updateComponent: (payload: any) => updateComponent(payload),
  updateComponents: (payload: any) => updateComponents(payload),
};

export const initializeComponentState = async () => {
  const shimmedConnection = new ShimmedConnection<ComponentDispatchers, State>(
    componentNamespace,
    [],
    reducer,
    dispatchers,
  );
  const OwcComponentConnection = await shimmedConnection.getConnection();

  return await connect(OwcComponentConnection);
};

export const getComponentStateConnection = async () => {
  await initializeComponentState();
  return await getConnection<ComponentDispatchers>(componentNamespace);
};

export const subscribeComponentState = async (id: string, listener: any) => {
  await initializeComponentState();
  return await subscribe(id, componentNamespace, listener);
};

export const buildItemTree = (set: Component[], id: string): TreeItem => {
  const found = set.find(i => i.id === id);
  let childComponents;
  if (found) {
    const arr = set.filter(item => found.childComponents?.includes(item.id));
    childComponents = arr.map(i => buildItemTree(set, i.id));
  }
  return Object.assign({}, found, { childComponents });
};

export const cleanObj = function(item: any) {
  return JSON.parse(JSON.stringify(item));
};
